import styled from '@emotion/styled';
import { forwardRef, ReactNode, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { DropdownItemProps } from 'react-bootstrap/DropdownItem';
import { theme } from '../../theme';
import { PlainButton } from '../buttons';

export type ActionMenuItem = {
  label: string;
  action: (e: React.MouseEvent<DropdownItemProps>) => any;
  dataCy?: string;
  disabled?: boolean;
};

export interface IActionMenuProps {
  toggleIcon: ReactNode;
  items: ActionMenuItem[];
  margin?: string;
  align?: 'left' | 'right';
  dataIcom?: string;
  dataCy?: string;
  menuDataIcom?: string;
  inline?: boolean;
  noMargin?: boolean;
  noBackground?: boolean;
  menuShiftX?: number;
  menuShiftY?: number;
}

interface IActionMenuButtonWithMarginProps {
  margin?: string;
  noMargin?: boolean;
  noBackground?: boolean;
}

const ActionMenuButtonWrap = styled(PlainButton)`
  margin: ${({ margin, noMargin }: IActionMenuButtonWithMarginProps) => (noMargin ? '0' : margin || '4px 0 0 0')};
  background-color: ${({ noBackground }: IActionMenuButtonWithMarginProps) =>
    noBackground ? 'transparent' : 'inherit'};
`;

// Create base menu style without transforms
const ActionMenuMenuBase = styled.div`
  box-shadow: 0 5px 30px -20px rgb(0 0 0 / 50%);
  border-radius: ${theme.borderRadius.m};
  margin-top: ${theme.spacing.d};
  padding: ${theme.spacing.d} 0;
  width: max-content;
  z-index: 100;
  :before,
  :after {
    display: none !important;
  }
`;

// Function to create a menu component with specific X and Y shifts
const createShiftedMenu = (shiftX?: number, shiftY?: number) => {
  // Only create a new styled component if we have shifts
  if (shiftX || shiftY) {
    return styled(ActionMenuMenuBase)`
      transform: translate(${shiftX ?? 0}px, ${shiftY ?? 0}px) !important;
    `;
  }
  return ActionMenuMenuBase;
};

const StyledActionMenuItem = styled.a`
  display: block;
  padding: ${theme.spacing.d} ${theme.spacing.f};
  cursor: pointer;
  font-family: ${theme.typography.body.medium.regular.family};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.body.medium.regular.fontSize};
  font-weight: ${theme.typography.body.medium.regular.fontWeight};
  line-height: ${theme.typography.body.medium.regular.lineHeight};
  :hover {
    text-decoration: none;
    background-color: ${theme.colors.backgroundBasic.hover};
    color: ${theme.colors.text.primary};
  }
`;

const ActionMenuButton = forwardRef(
  ({ children, onClick, margin, dataIcom, noMargin, noBackground }: any, ref: any) => (
    <ActionMenuButtonWrap
      data-icom={dataIcom}
      data-cy="menu-toggle"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      margin={margin}
      noMargin={noMargin}
      noBackground={noBackground}
    >
      {children}
    </ActionMenuButtonWrap>
  ),
);

const dropdownEventEmitter = {
  listeners: new Set<() => void>(),
  emit() {
    this.listeners.forEach((listener) => listener());
  },
  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
      return void 0;
    };
  },
};

export const ActionMenu = ({
  toggleIcon,
  items,
  margin,
  dataIcom,
  align,
  dataCy,
  menuDataIcom,
  inline,
  noMargin,
  noBackground,
  menuShiftX,
  menuShiftY,
}: IActionMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = dropdownEventEmitter.subscribe(() => {
      setIsOpen(false);
    });
    return unsubscribe;
  }, []);

  const handleToggle = (nextShow: boolean) => {
    if (nextShow) {
      dropdownEventEmitter.emit(); // Close other dropdowns
    }
    setIsOpen(nextShow);
  };

  // Create the menu component with the appropriate shifts
  const ShiftedActionMenuMenu = createShiftedMenu(menuShiftX, menuShiftY);

  return (
    <Dropdown show={isOpen} onToggle={handleToggle} data-cy={dataCy} className={inline ? 'd-inline-block' : undefined}>
      <Dropdown.Toggle
        as={ActionMenuButton}
        margin={margin}
        dataIcom={dataIcom}
        noMargin={noMargin}
        noBackground={noBackground}
      >
        {toggleIcon}
      </Dropdown.Toggle>
      <Dropdown.Menu
        as={ShiftedActionMenuMenu}
        align={align ?? 'left'}
        flip={false}
        alignRight={false}
        theme={theme}
        data-icom={menuDataIcom}
      >
        {items.map((item) => (
          <Dropdown.Item
            key={item.label}
            as={StyledActionMenuItem}
            theme={theme}
            bsPrefix={'action-menu-item'}
            data-cy={item.dataCy ? item.dataCy : 'menu-item'}
            onClick={item.action}
          >
            {item.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};
